import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough,
    Heading2, Heading3, List, ListOrdered, Quote,
    Link as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon,
    Undo2, Redo2, Eraser,
} from "lucide-react";

/**
 * Semantic WYSIWYG editor built on TipTap. Emits HTML via onChange(html).
 * Accepts `value` (HTML string). The toolbar lives on top; the right slot
 * is reserved for caller-provided AI action buttons.
 */
function ToolbarBtn({ onClick, active, disabled, label, children, testId }) {
    return (
        <Button
            type="button"
            variant={active ? "default" : "ghost"}
            size="sm"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            className="h-8 w-8 p-0"
            data-testid={testId}
        >
            {children}
        </Button>
    );
}

export default function RichTextEditor({
    value = "",
    onChange,
    placeholder = "Start writing your lesson…",
    rightSlot = null,
    minHeight = 320,
    disabled = false,
}) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                codeBlock: false,
                link: false,
                underline: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
            }),
            Image.configure({ inline: false, allowBase64: false }),
            Youtube.configure({ controls: true, nocookie: true, width: 640, height: 360 }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        editable: !disabled,
        editorProps: {
            attributes: {
                class:
                    "tiptap-editor prose prose-sm max-w-none focus:outline-none px-4 py-3 text-foreground/90 leading-relaxed",
                "data-testid": "rich-text-editor-area",
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            // TipTap returns "<p></p>" for empty — normalise to empty string.
            onChange?.(html === "<p></p>" ? "" : html);
        },
    });

    // Sync external value changes (e.g., AI-generated content replacing the editor body)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const next = value || "";
        if (next && next !== current) {
            editor.commands.setContent(next, false);
        } else if (!next && current !== "<p></p>" && current !== "") {
            editor.commands.clearContent();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    const addLink = useCallback(() => {
        if (!editor) return;
        const previous = editor.getAttributes("link").href || "";
        const url = window.prompt("Enter URL", previous);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
    }, [editor]);

    const addImage = useCallback(() => {
        if (!editor) return;
        const url = window.prompt("Paste an image URL (https://…)");
        if (!url) return;
        editor.chain().focus().setImage({ src: url, alt: "" }).run();
    }, [editor]);

    const addYoutube = useCallback(() => {
        if (!editor) return;
        const url = window.prompt("Paste a YouTube URL");
        if (!url) return;
        editor.commands.setYoutubeVideo({ src: url, width: 640, height: 360 });
    }, [editor]);

    if (!editor) {
        return (
            <div
                className="rounded-md border border-input bg-background"
                style={{ minHeight }}
                data-testid="rich-text-editor-loading"
            />
        );
    }

    const isActive = (name, attrs) => editor.isActive(name, attrs);

    return (
        <div
            className="rounded-md border border-input bg-background overflow-hidden"
            data-testid="rich-text-editor"
        >
            <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/40 px-2 py-1.5">
                <ToolbarBtn
                    label="Heading 2"
                    active={isActive("heading", { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    testId="rte-h2"
                >
                    <Heading2 className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Heading 3"
                    active={isActive("heading", { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    testId="rte-h3"
                >
                    <Heading3 className="h-4 w-4" />
                </ToolbarBtn>
                <Separator orientation="vertical" className="mx-1 h-5" />
                <ToolbarBtn
                    label="Bold"
                    active={isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    testId="rte-bold"
                >
                    <Bold className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Italic"
                    active={isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    testId="rte-italic"
                >
                    <Italic className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Underline"
                    active={isActive("underline")}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    testId="rte-underline"
                >
                    <UnderlineIcon className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Strikethrough"
                    active={isActive("strike")}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    testId="rte-strike"
                >
                    <Strikethrough className="h-4 w-4" />
                </ToolbarBtn>
                <Separator orientation="vertical" className="mx-1 h-5" />
                <ToolbarBtn
                    label="Bulleted list"
                    active={isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    testId="rte-ul"
                >
                    <List className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Numbered list"
                    active={isActive("orderedList")}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    testId="rte-ol"
                >
                    <ListOrdered className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Quote"
                    active={isActive("blockquote")}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    testId="rte-quote"
                >
                    <Quote className="h-4 w-4" />
                </ToolbarBtn>
                <Separator orientation="vertical" className="mx-1 h-5" />
                <ToolbarBtn
                    label="Insert link"
                    active={isActive("link")}
                    onClick={addLink}
                    testId="rte-link"
                >
                    <LinkIcon className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn label="Insert image" onClick={addImage} testId="rte-image">
                    <ImageIcon className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn label="Embed YouTube" onClick={addYoutube} testId="rte-youtube">
                    <YoutubeIcon className="h-4 w-4" />
                </ToolbarBtn>
                <Separator orientation="vertical" className="mx-1 h-5" />
                <ToolbarBtn
                    label="Undo"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    testId="rte-undo"
                >
                    <Undo2 className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Redo"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    testId="rte-redo"
                >
                    <Redo2 className="h-4 w-4" />
                </ToolbarBtn>
                <ToolbarBtn
                    label="Clear formatting"
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                    testId="rte-clear"
                >
                    <Eraser className="h-4 w-4" />
                </ToolbarBtn>

                {rightSlot ? (
                    <div className="ml-auto flex items-center gap-2">{rightSlot}</div>
                ) : null}
            </div>

            <div style={{ minHeight }} className="cursor-text">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
